import { Alert, Button, ConfigProvider, Form, Input, theme as antTheme } from "antd";
import { Navigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { LockKeyhole, ShieldCheck, User } from "lucide-react";

import { selectAuthError, selectAuthStatus, selectUser, signIn } from "@/app/authSlice";
import { roleHome } from "@/utils/roleHome";

/**
 * The sign-in screen, after BMR_Frontend's AuthWrapper.js: a full-bleed
 * industrial backdrop under a dark gradient, the product mark fixed to the
 * top-left, and the credentials card floated to the right half.
 *
 * The backdrop is drawn in CSS rather than loaded as a wallpaper — layered
 * radial washes over the plant teal, with a faint blueprint grid across it.
 * It costs no image request and cannot fail to load, which on the one screen
 * standing between an operator and the line is worth more than a photograph.
 *
 * Sizes here are px, not rem: this screen renders before the app shell, so
 * the font-size toggle's root scaling does not apply to it and the layout
 * should not shift if it later does.
 */

const PITCH = [
  "Optimized schedules you can trust — every number measured, never estimated",
  "Campaign-aware cleaning & changeover",
  "What-if sandbox with cost-of-change insight",
  "Committed plans flow back to the eBMR",
];

export default function LoginPage() {
  const dispatch = useDispatch();
  const status = useSelector(selectAuthStatus);
  const error = useSelector(selectAuthError);
  const user = useSelector(selectUser);

  // Supervisors are the primary users of the execution surface — they land
  // where their work is; everyone else lands on the portfolio overview.
  if (status === "authenticated") {
    return (
      <Navigate to={roleHome(user?.role)} replace />
    );
  }

  return (
    // The card is a white surface whatever the app theme is, so its controls
    // are pinned to the light algorithm here — AuthWrapper.js forces light
    // mode on this screen for the same reason. Without it, dark mode would
    // render dark inputs and near-white label text on white.
    <ConfigProvider
      theme={{
        algorithm: antTheme.defaultAlgorithm,
        token: { colorPrimary: "#1666d0", borderRadius: 8 },
        components: {
          Input: { controlHeight: 44 },
          Button: { controlHeight: 44, fontWeight: 600, primaryShadow: "none" },
        },
      }}
    >
    <div className="relative flex min-h-screen w-full items-center justify-center overflow-hidden lg:justify-end">
      {/* The plant backdrop: a deep teal base lit from two corners. */}
      <div
        className="absolute inset-0"
        style={{
          background:
            "radial-gradient(1200px 600px at 12% 18%, #164e8c 0%, transparent 60%)," +
            "radial-gradient(900px 700px at 88% 88%, #1c4f57 0%, transparent 55%)," +
            "linear-gradient(150deg, #081627 0%, #0a2545 45%, #0e3a6b 100%)",
        }}
      />

      {/* Blueprint grid — the one texture that reads as engineering drawing
          rather than decoration. Masked so it fades out toward the card and
          never competes with the form. */}
      <div
        className="absolute inset-0"
        style={{
          backgroundImage:
            "linear-gradient(rgba(255,255,255,0.055) 1px, transparent 1px)," +
            "linear-gradient(90deg, rgba(255,255,255,0.055) 1px, transparent 1px)",
          backgroundSize: "56px 56px",
          maskImage:
            "radial-gradient(1000px 800px at 25% 45%, #000 0%, transparent 78%)",
          WebkitMaskImage:
            "radial-gradient(1000px 800px at 25% 45%, #000 0%, transparent 78%)",
        }}
      />

      {/* Legibility wash under the card half. */}
      <div
        className="absolute inset-0"
        style={{
          background:
            "linear-gradient(to right, rgba(6,20,22,0.30) 0%, rgba(6,20,22,0.62) 100%)",
        }}
      />

      {/* ---- Brand bar, fixed to the top as in AuthWrapper.js ---- */}
      <header
        className="absolute inset-x-0 top-0 z-20 flex items-center gap-4 px-6 py-5"
        style={{
          background:
            "linear-gradient(to bottom, rgba(0,0,0,0.38) 0%, transparent 100%)",
        }}
      >
        <div
          className="flex shrink-0 items-center justify-center rounded-xl"
          style={{
            height: 48,
            width: 48,
            background: "linear-gradient(135deg, #5b87b5 0%, #164e8c 100%)",
            boxShadow: "0 4px 15px rgba(10,40,44,0.55)",
          }}
        >
          <span className="font-bold text-white" style={{ fontSize: 22 }}>
            N
          </span>
        </div>
        <div className="min-w-0">
          <h1
            className="m-0 font-bold tracking-wide text-white"
            style={{ fontSize: 22, lineHeight: 1.2 }}
          >
            eBMR <span style={{ color: "#7fd4dd" }}>Scheduler</span>
          </h1>
          <p
            className="m-0 hidden tracking-wider text-white/65 sm:block"
            style={{ fontSize: 11.5 }}
          >
            Constraint-based planning for pharmaceutical batch manufacturing
          </p>
        </div>
      </header>

      {/* ---- Product pitch, the left half of the split ---- */}
      <div className="relative z-10 hidden flex-1 px-14 lg:block">
        <div style={{ maxWidth: 460 }}>
          <div
            className="inline-flex items-center gap-2 rounded-full px-3 py-1"
            style={{
              border: "1px solid rgba(255,255,255,0.22)",
              background: "rgba(255,255,255,0.07)",
            }}
          >
            <ShieldCheck size={14} strokeWidth={2.25} color="#7fd4dd" />
            <span
              className="font-semibold uppercase tracking-wider text-white/80"
              style={{ fontSize: 10.5 }}
            >
              21 CFR Part 11 · EU Annex 11
            </span>
          </div>

          <h2
            className="m-0 mt-6 font-bold tracking-tight text-white"
            style={{ fontSize: 38, lineHeight: 1.15 }}
          >
            Plan the plant,
            <br />
            not the spreadsheet.
          </h2>

          <p
            className="mt-4 text-white/70"
            style={{ fontSize: 14.5, lineHeight: 1.7 }}
          >
            Constraint-based scheduling built on the Nexgensis eBMR — every
            plan feasible against equipment, changeover and workforce before
            it ever reaches the floor.
          </p>

          <ul className="mt-7 space-y-3">
            {PITCH.map((line) => (
              <li key={line} className="flex items-start gap-3">
                <span
                  className="mt-1.5 shrink-0 rounded-sm"
                  style={{ height: 6, width: 6, background: "#7fd4dd" }}
                />
                <span className="text-white/75" style={{ fontSize: 13.5 }}>
                  {line}
                </span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* ---- Credentials card ---- */}
      <div className="relative z-10 flex w-full items-center justify-center p-6 lg:w-[46%]">
        <div
          className="w-full rounded-2xl"
          style={{
            maxWidth: 420,
            padding: 32,
            background: "rgba(255,255,255,0.97)",
            backdropFilter: "blur(8px)",
            WebkitBackdropFilter: "blur(8px)",
            boxShadow: "0 25px 50px -12px rgba(0,0,0,0.45)",
          }}
        >
          {/* The brand repeats inside the card on small screens, where the
              header above is the only other place it appears. */}
          <div className="mb-6 text-center lg:hidden">
            <h1
              className="m-0 font-bold tracking-wide"
              style={{ fontSize: 24, color: "#0e3a6b" }}
            >
              eBMR Scheduler
            </h1>
          </div>

          <h2
            className="m-0 font-bold"
            style={{ fontSize: 24, color: "#0f172a", lineHeight: 1.25 }}
          >
            Sign in
          </h2>
          <p className="m-0 mt-1.5" style={{ fontSize: 13, color: "#64748b" }}>
            Use your plant credentials to continue
          </p>

          {error && (
            <Alert
              type="error"
              showIcon
              message={error}
              style={{ marginTop: 18 }}
            />
          )}

          <Form
            layout="vertical"
            requiredMark={false}
            style={{ marginTop: 22 }}
            onFinish={(values) => dispatch(signIn(values))}
          >
            <Form.Item
              name="username"
              label={
                <span style={{ fontSize: 12.5, fontWeight: 600, color: "#334155" }}>
                  Username
                </span>
              }
              rules={[{ required: true, message: "Enter your username" }]}
            >
              <Input
                size="large"
                autoFocus
                autoComplete="username"
                placeholder="Username"
                prefix={
                  <User size={16} strokeWidth={2.25} color="#94a3b8" />
                }
              />
            </Form.Item>

            <Form.Item
              name="password"
              label={
                <span style={{ fontSize: 12.5, fontWeight: 600, color: "#334155" }}>
                  Password
                </span>
              }
              rules={[{ required: true, message: "Enter your password" }]}
            >
              <Input.Password
                size="large"
                autoComplete="current-password"
                placeholder="••••••••"
                prefix={
                  <LockKeyhole size={16} strokeWidth={2.25} color="#94a3b8" />
                }
              />
            </Form.Item>

            <Form.Item style={{ marginBottom: 0, marginTop: 26 }}>
              <Button
                type="primary"
                htmlType="submit"
                size="large"
                block
                loading={status === "loading"}
                className="font-semibold"
                style={{ height: 44, background: "#0e3a6b", borderColor: "#0e3a6b" }}
              >
                Sign in
              </Button>
            </Form.Item>
          </Form>

          <div
            className="mt-6 text-center"
            style={{ fontSize: 11.5, color: "#94a3b8" }}
          >
            Sign-in integrates with the eBMR&apos;s own SSO.
          </div>
        </div>
      </div>

      {/* ---- Compliance footer ---- */}
      <footer
        className="absolute inset-x-0 bottom-0 z-10 px-6 py-4 text-center"
        style={{ fontSize: 11, color: "rgba(255,255,255,0.42)" }}
      >
        © 2026 Nexgensis Technologies · All rights reserved · Planning metadata
        only — GMP execution stays in the eBMR
      </footer>
    </div>
    </ConfigProvider>
  );
}
